import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

/**
 * @-mention autocomplete for the room composer.
 * - Detects when the input's caret sits inside an `@partial` token.
 * - Filters `members` (Who's Here list) by prefix, case-insensitive.
 * - Renders a floating dropdown above the input. Keyboard: ArrowUp/Down/Enter/Escape.
 *
 * Controlled component — the parent owns the input value and passes it in.
 * When the user selects a handle we call onSelect(newBody, newCaret).
 */
export default function MentionAutocomplete({
  inputRef,
  value,
  members,
  myId,
  onSelect,
  onKeyBind, // (handler) => void — parent calls handler(e) inside its own onKeyDown
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [tokenStart, setTokenStart] = useState(-1);
  const [prefix, setPrefix] = useState("");
  const activeRef = useRef(0);

  // Parse the current token around the caret. Returns {start, prefix} or null.
  const parseToken = useCallback(() => {
    const el = inputRef.current;
    if (!el) return null;
    const caret = el.selectionStart ?? (value?.length || 0);
    const before = value.slice(0, caret);
    const at = before.lastIndexOf("@");
    if (at < 0) return null;
    // Must be at start OR preceded by whitespace to count as a mention trigger.
    if (at > 0 && !/\s/.test(before[at - 1])) return null;
    const partial = before.slice(at + 1);
    // Bail if the partial contains whitespace (user moved past the token).
    if (/\s/.test(partial)) return null;
    if (partial.length > 24) return null;
    return { start: at, prefix: partial };
  }, [inputRef, value]);

  useEffect(() => {
    const tok = parseToken();
    if (!tok) {
      setOpen(false);
      setTokenStart(-1);
      setPrefix("");
      return;
    }
    setTokenStart(tok.start);
    setPrefix(tok.prefix);
    setOpen(true);
    setActive(0);
    activeRef.current = 0;
  }, [value, parseToken]);

  const filtered = useMemo(() => {
    const pl = prefix.toLowerCase();
    return (members || [])
      .filter((m) => m.id !== myId)
      .filter((m) => (m.username || "").toLowerCase().startsWith(pl))
      .slice(0, 6);
  }, [members, prefix, myId]);

  const pick = useCallback((mem) => {
    if (!mem) return;
    const el = inputRef.current;
    const caret = el?.selectionStart ?? value.length;
    const before = value.slice(0, tokenStart);
    const after = value.slice(caret);
    const replacement = `@${mem.username} `;
    const next = before + replacement + after;
    const newCaret = (before + replacement).length;
    onSelect(next, newCaret);
    setOpen(false);
  }, [inputRef, onSelect, tokenStart, value]);

  // Register key handler with parent so ArrowUp/Down/Enter can intercept the input.
  useEffect(() => {
    if (!onKeyBind) return;
    const handler = (e) => {
      if (!open || filtered.length === 0) return false;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const nx = (activeRef.current + 1) % filtered.length;
        activeRef.current = nx;
        setActive(nx);
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        const nx = (activeRef.current - 1 + filtered.length) % filtered.length;
        activeRef.current = nx;
        setActive(nx);
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(filtered[activeRef.current]);
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return true;
      }
      return false;
    };
    onKeyBind(handler);
    return () => onKeyBind(null);
  }, [onKeyBind, open, filtered, pick]);

  if (!open || filtered.length === 0) return null;

  return (
    <div
      data-testid="mention-autocomplete"
      className="absolute bottom-full mb-2 left-4 md:left-6 z-40 min-w-[240px] bg-obsidian border-2 border-acid shadow-[6px_6px_0_#00E5FF]"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="font-mono text-[10px] tracking-widest text-acid px-3 py-2 border-b border-white/10">
        // WHO&apos;S HERE · @{prefix || "…"}
      </div>
      <ul className="max-h-56 overflow-y-auto">
        {filtered.map((m, i) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => pick(m)}
              data-testid={`mention-option-${m.username}`}
              className={`w-full text-left flex items-center gap-3 px-3 py-2 font-mono text-sm ${
                i === active ? "bg-acid text-obsidian" : "text-white/80 hover:bg-white/5"
              }`}
            >
              {m.avatar && (
                <img
                  src={m.avatar}
                  alt=""
                  className="w-6 h-6 object-cover border border-white/10 shrink-0"
                />
              )}
              <span className="truncate">@{m.username}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
